import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { useState } from 'react';
import { DataTestID } from 'src/utils';

import { NoResultAlert } from '../noResultAlert';
import { Progress } from '../progress';
import { CustomTableProps } from './CustomTable.interface';
import {
  StyledTableCell,
  StyledTableCellHead,
  TableBodyContainer,
} from './CustomTable.styled';
import { CustomTableRow } from './CustomTableRow';

export const CustomTable = <
  T extends Record<string, any> = Record<string, any>,
>({
  columns,
  rows,
  loading = false,
  stickyHeader = true,
  emptyMessage,
  renderActions,
  noBorder,
  renderTags,
  overrideHeight,
  renderHeadActions,
  renderCollapseContent,
  tableCellHeadProps,
  tableBodyRowProps,
  expandedRowIndex: externalExpandedRowIndex,
  onRowExpand: externalOnRowExpand,
  onRowClick,
  tableHeadRenderer,
  customCellRenderer,
  renderAccordionRow,
  scrollContainerRef,
  bodyBackgroundColor,
}: CustomTableProps<T>) => {
  const [internalExpandedRowIndex, setInternalExpandedRowIndex] =
    useState<number>(-1);

  const expandedRowIndex =
    externalExpandedRowIndex !== undefined
      ? externalExpandedRowIndex
      : internalExpandedRowIndex;

  const handleRowExpand = (index: number) => {
    const newIndex = expandedRowIndex === index ? -1 : index;
    if (externalOnRowExpand) {
      externalOnRowExpand(newIndex);
    } else {
      setInternalExpandedRowIndex(newIndex);
    }
  };

  return (
    <TableBodyContainer
      ref={scrollContainerRef}
      component={Paper}
      $bodyBackgroundColor={bodyBackgroundColor}
      $noBorder={overrideHeight === '0px' || noBorder}
      sx={overrideHeight ? { height: overrideHeight } : {}}>
      <Table
        stickyHeader={stickyHeader}
        data-testid={DataTestID.CustomTable}
        sx={{ tableLayout: 'fixed', width: '100%' }}>
        {!loading ? (
          tableHeadRenderer ? (
            tableHeadRenderer(columns)
          ) : (
            <TableHead>
              <TableRow>
                {columns.map((col, idx) => (
                  <StyledTableCellHead
                    key={idx}
                    align={col.align || 'left'}
                    {...tableCellHeadProps}
                    sx={{ width: col.width, ...tableCellHeadProps?.sx }}>
                    {col.id === 'actions'
                      ? renderHeadActions?.(col)
                      : col.label || ''}
                  </StyledTableCellHead>
                ))}
              </TableRow>
            </TableHead>
          )
        ) : null}

        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell
                colSpan={
                  renderAccordionRow ? columns.length + 2 : columns.length
                }
                align="center">
                <Box display="flex" justifyContent="center" py={2}>
                  <Progress />
                </Box>
              </TableCell>
            </TableRow>
          ) : rows.length === 0 ? (
            <TableRow>
              <StyledTableCell
                colSpan={
                  renderAccordionRow ? columns.length + 2 : columns.length
                }
                align="center">
                <NoResultAlert message={emptyMessage} />
              </StyledTableCell>
            </TableRow>
          ) : (
            rows.map((row, idx) => {
              if (renderAccordionRow) {
                return renderAccordionRow(row, idx);
              } else {
                return (
                  <CustomTableRow<T>
                    key={idx}
                    columns={columns}
                    row={row}
                    renderActions={renderActions}
                    renderTags={renderTags}
                    renderCollapseContent={renderCollapseContent}
                    expanded={expandedRowIndex === idx}
                    onToggle={() => handleRowExpand(idx)}
                    onRowClick={onRowClick}
                    tableRowProps={tableBodyRowProps}
                    customCellRenderer={customCellRenderer}
                  />
                );
              }
            })
          )}
        </TableBody>
      </Table>
    </TableBodyContainer>
  );
};
